// src/utils/firebase/user.js
import { 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc 
} from 'firebase/firestore';
import { db } from './firebase.utils';
import { logUserAction } from './activity';
import { serializeFirestoreData } from './serialization';
import { syncPublicProfile } from './publicProfile';

/**
 * Updates user's favorite team
 * @param {string} userID - User ID
 * @param {number} newTeamIndex - New team index
 * @param {number} currentTeamIndex - Current team index
 */
export const updateTeamIndex = async (userID, newTeamIndex, currentTeamIndex) => {
  try {
    const userDocRef = doc(db, `users/${userID}`);
    await updateDoc(userDocRef, { teamIndex: newTeamIndex });
    logUserAction(userID, 'updatedTeam', { oldTeam: currentTeamIndex, newTeam: newTeamIndex });
  } catch (error) {
    console.error('Error updating favorite team:', error);
  }
};

/**
 * Fetches user data from Firestore. Only readable by the user themselves —
 * use fetchPublicProfile in publicProfile.js to read another user.
 * @param {string} userID - User ID
 * @returns {Promise<Object|null>} User data or null if not found
 */
export const fetchUserFromFirestore = async (userID) => {
  const userDocRef = doc(db, `users/${userID}`);
  const userSnap = await getDoc(userDocRef);
  return userSnap.exists() ? serializeFirestoreData(userSnap.data()) : null;
};

/**
 * Updates user document in Firestore
 * @param {string} userID - User ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<void>}
 */
export const updateUserDocument = async (userID, updateData) => {
  try {
    const userDocRef = doc(db, `users/${userID}`);
    await updateDoc(userDocRef, updateData);
  } catch (error) {
    console.error('Error updating user document:', error);
    throw error;
  }
};

/**
 * Creates a new user document
 * @param {string} userID - User ID
 * @param {Object} userData - Initial user data
 * @returns {Promise<void>}
 */
export const createUserDocument = async (userID, userData) => {
  try {
    const userDocRef = doc(db, `users/${userID}`);
    const newUserData = {
      email: userData.email,
      teamIndex: 0,
      collectionCardCount: 0,
      tokens: 0,
      createdAt: new Date().toISOString(),
      ...userData
    };
    await setDoc(userDocRef, newUserData);
    await syncPublicProfile(userID, newUserData);
  } catch (error) {
    console.error('Error creating user document:', error);
    throw error;
  }
};
